/**
 * Old-device picker — the operator picks the device being replaced from the
 * catalog the same way the new device is picked, with a free-text fallback
 * for a model the catalog no longer carries.
 *
 * Functional spec: `docs/features/REPLACEMENT-PLANNING.md`, "Old device
 * picker". Shared between the plan editor's per-line grid cell
 * (`ReplacementPlanEditor.tsx`) and the guided add-line wizard
 * (`ReplacementLineWizard.tsx`), so the two surfaces never grow a second,
 * drifting way to resolve a catalog pick into `old_label`/`old_sku`.
 *
 * `old_label`/`old_sku` stay free text server-side
 * (`ReplacementPlanLineIn`/`PlanLineComputedOut`, `webapi/schemas.py`) — this
 * component only ever *fills* them from a catalog pick, it never enforces
 * that they still match one afterward, and the display mode below is driven
 * purely by whether `old_sku` is currently set, never by "how it got set":
 * a CSV-imported `old_sku` (`utils/replacement_import.py`, never resolved
 * against the catalog) looks exactly like a picker-resolved one, because the
 * API has no third field to remember the difference across a reload. Once
 * `old_sku` is set the cell shows a button (name + SKU) mirroring the
 * new-item cell; its small pencil action clears `old_sku` only, dropping
 * back to a plain text input without discarding whatever label text is
 * already there — "device not in the catalog" never loses the label.
 */
import { useState } from 'react'
import {
  ActionIcon, Button, Group, Stack, Text, TextInput, Tooltip,
} from '@mantine/core'

import { api } from '../api/client'
import type { InventoryLine, ItemDetail, ItemRow } from '../api/types'
import { ItemPicker } from '../pages/ItemPicker'
import { fmtQty } from '../format'
import { CODE_STYLE } from '../theme'

export interface OldDeviceValue {
  old_label: string
  old_sku: string | null
}

interface Props {
  value: OldDeviceValue
  onChange: (patch: Partial<OldDeviceValue>) => void
  disabled?: boolean
  /**
   * Lines of the plan's currently selected snapshot, if any — passed through
   * to `onHandForSku` so the cell can show an informational on-hand hint.
   * `undefined` (no snapshot selected) simply suppresses the hint.
   */
  snapshotLines?: InventoryLine[]
  size?: 'xs' | 'sm'
}

/**
 * The old device's own on-hand quantity in `lines`, or `null` when its SKU is
 * unset or not present in the snapshot at all — "exists in it" per the design
 * doc, not "exists with a positive quantity". Only unfrozen lines count
 * (`element_name` blank), the same rule `InventorySnapshot.qty_for_sku()`
 * applies server-side (`database/models.py`) for every other on-hand figure
 * this screen shows, so the hint never implies more stock is free than the
 * engine itself would see.
 */
export function onHandForSku(
  lines: InventoryLine[] | undefined, sku: string | null,
): number | null {
  if (!sku || !lines?.length) return null
  const matching = lines.filter((l) => !l.element_name && l.sku === sku)
  if (!matching.length) return null
  return matching.reduce((sum, l) => sum + l.qty_on_hand, 0)
}

/**
 * A catalog pick -> the `old_label`/`old_sku` it resolves to. Exported so the
 * wizard's step 1 can reuse the exact same resolution `OldDeviceField` itself
 * uses.
 *
 * `old_label` gets the manufacturer appended when the picker exposes one
 * (design doc) because, unlike the new-item side (which carries `item_name`
 * and `item_manufacturer` as separate fields), there is no structured field
 * for it here — this free-text label is the only place it can go.
 *
 * The SKU is the item's *first* component's vendor SKU (`components[0].sku`)
 * — the same "first component" convention the server already uses for a
 * catalog row's derived `internal_sku`/`description`
 * (`webapi/routers/items.py::list_items`), and the same field a
 * replacement-import CSV's `new_sku` resolves against
 * (`utils/replacement_import.py::_resolve_new_sku`, which matches
 * `components.sku`) — so a value filled in here lines up with both of those
 * rather than inventing a third convention.
 *
 * The catalog's own list endpoint (what `ItemPicker` searches) deliberately
 * omits `components` for payload size (`ItemRowOut`'s own docstring), so this
 * needs its own `GET /items/{id}` to see them. Never throws: an item with no
 * components, or a lookup that fails, still fills `old_label` and leaves
 * `old_sku` null rather than blocking the pick.
 */
export async function resolveOldDevicePick(item: ItemRow): Promise<OldDeviceValue> {
  const old_label = item.manufacturer ? `${item.name} (${item.manufacturer})` : item.name
  let old_sku: string | null = null
  try {
    const detail = await api.get<ItemDetail>(`/items/${item.id}`)
    old_sku = detail.components[0]?.sku?.trim() || null
  } catch {
    old_sku = null
  }
  return { old_label, old_sku }
}

export function OldDeviceField({
  value, onChange, disabled, snapshotLines, size = 'xs',
}: Props) {
  const [picking, setPicking] = useState(false)
  const onHand = onHandForSku(snapshotLines, value.old_sku)

  async function pick(item: ItemRow) {
    onChange(await resolveOldDevicePick(item))
  }

  return (
    <>
      <Stack gap={2} py={2}>
        {value.old_sku ? (
          <Group gap={4} wrap="nowrap" align="flex-start">
            <Button
              size={size}
              variant={disabled ? 'subtle' : 'light'}
              disabled={disabled}
              onClick={() => setPicking(true)}
              style={{ height: 'auto', flex: 1, paddingTop: 4, paddingBottom: 4 }}
            >
              <Stack gap={0} style={{ width: '100%' }}>
                <Text size={size} fw={500} ta="center" lineClamp={1}>
                  {value.old_label || 'ללא שם'}
                </Text>
                <Text size="10px" style={{ ...CODE_STYLE, textAlign: 'center' }}>
                  {value.old_sku}
                </Text>
              </Stack>
            </Button>
            {!disabled && (
              <Tooltip label="המכשיר אינו בקטלוג — עבור להזנה ידנית">
                <ActionIcon
                  size={28} variant="subtle" color="gray" aria-label="הזנה ידנית"
                  onClick={() => onChange({ old_sku: null })}
                >
                  ✎
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        ) : (
          <Group gap={4} wrap="nowrap">
            <TextInput
              size={size}
              style={{ flex: 1 }}
              disabled={disabled}
              placeholder="שם המכשיר הישן…"
              value={value.old_label}
              onChange={(e) => onChange({ old_label: e.currentTarget.value })}
            />
            {!disabled && (
              <Tooltip label="בחר מהקטלוג">
                <ActionIcon
                  size={28} variant="light" aria-label="בחר מכשיר ישן מהקטלוג"
                  onClick={() => setPicking(true)}
                >
                  ⌕
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
        {onHand != null && (
          <Text size="10px" c="dimmed" ta="center">במלאי: {fmtQty(onHand)}</Text>
        )}
      </Stack>

      <ItemPicker
        opened={picking}
        onClose={() => setPicking(false)}
        onPick={(items) => {
          const item = items[0]
          setPicking(false)
          if (item) void pick(item)
        }}
        title="בחירת המכשיר הישן מהקטלוג"
        confirmLabel="בחר"
      />
    </>
  )
}
